import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Luxury_Sedan", "Premium_SUV", "Executive_Sprinter", "Motor_Coach"];

export default function AdminPricing() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [orig, setOrig] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pricing");
      setCfg(data); setOrig(JSON.parse(JSON.stringify(data)));
    } catch (e) { setErr(formatApiError(e)); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const computePreview = useCallback(async (c) => {
    if (!c) return;
    try {
      const noon = new Date(); noon.setHours(12, 0, 0, 0);
      const { data } = await api.post("/quotes", {
        service_type: "airport",
        pickup_address: "Times Square, Manhattan, NY",
        dropoff_address: "JFK Airport",
        pickup_datetime: noon.toISOString().slice(0, 19),
        vehicle_category: "Luxury_Sedan",
      });
      setPreviewQuote(data.quote);
    } catch {/* */}
  }, []);

  useEffect(() => { computePreview(cfg); }, [cfg, computePreview]);

  if (user && user.role !== "admin") return <Navigate to="/admin" replace />;
  if (!cfg) return <div className="text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">Loading pricing…</div>;

  const setClass = (cat, field, val) => {
    setCfg({ ...cfg, classes: { ...cfg.classes, [cat]: { ...cfg.classes[cat], [field]: Number(val) } } });
  };
  const setNum = (k, v) => setCfg({ ...cfg, [k]: Number(v) });

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.put("/admin/pricing", cfg);
      setOrig(JSON.parse(JSON.stringify(cfg)));
      setSavedAt(new Date());
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const dirty = JSON.stringify(cfg) !== JSON.stringify(orig);

  return (
    <div data-testid="admin-pricing" className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-2">Pricing</div>
          <h1 className="font-serif text-3xl font-semibold tracking-tighter">Rate card</h1>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">Saved</span>}
          <button onClick={save} disabled={!dirty || saving} data-testid="admin-pricing-save"
            className="bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-40 px-6 py-2.5 text-xs uppercase tracking-[0.25em]">
            {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
          </button>
        </div>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Class rates */}
          <div className="bg-[#121214]/60 border border-white/10 p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-4">Class rates</div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
                <tr><th className="text-left py-2">Category</th><th>Base</th><th>$/mile</th><th>$/hour</th></tr>
              </thead>
              <tbody>
                {CATEGORIES.map(cat => (
                  <tr key={cat} className="border-t border-white/5">
                    <td className="py-2 font-light">{cat.replace(/_/g, " ")}</td>
                    {["base","per_mile","per_hour"].map(f => (
                      <td key={f} className="py-2 px-2">
                        <input type="number" step="0.01" value={cfg.classes[cat]?.[f] ?? 0}
                          onChange={(e) => setClass(cat, f, e.target.value)}
                          data-testid={`pricing-${cat.toLowerCase()}-${f}`}
                          className="w-24 bg-[#0B0B0C] border border-white/15 px-2 py-1 text-sm" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Surcharges & gratuity */}
          <div className="bg-[#121214]/60 border border-white/10 p-5 grid grid-cols-2 gap-4">
            <Num label="Airport surcharge ($)" value={cfg.airport_surcharge_amount} onChange={v => setNum("airport_surcharge_amount", v)} testid="pricing-airport" />
            <Num label="Gratuity (0–0.5)" step="0.01" value={cfg.gratuity_pct} onChange={v => setNum("gratuity_pct", v)} testid="pricing-gratuity" />
            <Num label="Late-night multiplier" step="0.01" value={cfg.late_night_multiplier} onChange={v => setNum("late_night_multiplier", v)} testid="pricing-late-mult" />
            <Num label="Hourly minimum (hrs)" value={cfg.hourly_minimum_hours} onChange={v => setNum("hourly_minimum_hours", v)} testid="pricing-hourly-min" />
            <Num label="Late-night start hour" value={cfg.late_night_start_hour} onChange={v => setNum("late_night_start_hour", v)} testid="pricing-late-start" />
            <Num label="Late-night end hour" value={cfg.late_night_end_hour} onChange={v => setNum("late_night_end_hour", v)} testid="pricing-late-end" />
          </div>
        </div>

        {/* Preview */}
        <div className="bg-[#121214]/60 border border-white/10 p-5 h-fit sticky top-20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-3">Preview · Manhattan → JFK · Sedan · Noon</div>
          {previewQuote ? (
            <div data-testid="admin-pricing-preview" className="space-y-2 text-sm">
              {previewQuote.breakdown.map((b, i) => (
                <div key={i} className={`flex justify-between ${b.label === "Total" ? "pt-2 mt-1 border-t border-white/15 text-base" : ""}`}>
                  <span className="text-[#E6E6E6]">{b.label}</span>
                  <span className="tabular-nums">${b.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : <div className="text-[#8E9AA6] text-xs">Computing…</div>}
          <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
            Save → public quote engine refreshes in ≤30 s.
          </div>
        </div>
      </div>
    </div>
  );
}

function Num({ label, value, onChange, testid, step = "1" }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#8E9AA6] mb-1.5">{label}</label>
      <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#0B0B0C] border border-white/15 px-3 py-2 text-sm focus:border-white focus:outline-none" />
    </div>
  );
}
